import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { ClipCard, ClipModal } from '../components/Clips.jsx'
import { HeartIcon } from '../components/Icons.jsx'
import { pickI18n } from '../i18n'
import { timeAgo } from '../lib.js'
import { enablePush } from '../push.js'
import { ErrorState, Loading } from './states.jsx'

const KINDS = ['unwell', 'injured', 'missing', 'other']

function ReportForm({ catId, onDone }) {
  const { t } = useTranslation()
  const [kind, setKind] = useState('unwell')
  const [text, setText] = useState('')
  const [state, setState] = useState({ busy: false, error: null, sent: false })
  const send = async (e) => {
    e.preventDefault()
    setState({ busy: true, error: null, sent: false })
    try {
      await api.reportCat(catId, { kind, text: text.trim() })
      setState({ busy: false, error: null, sent: true })
      onDone()
    } catch (err) {
      setState({ busy: false, error: err.status === 429 ? t('catPage.tooMany') : err.message, sent: false })
    }
  }
  if (state.sent) return <p className="m-0 rounded-2xl bg-mint p-4 font-bold">{t('catPage.reportSent')}</p>
  return (
    <form onSubmit={send} className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-live/40 p-4">
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
            className={`cursor-pointer rounded-full px-3.5 py-2 text-sm font-extrabold ${kind === k ? 'bg-ink text-on-ink' : 'border-[1.5px] border-edge text-ink'}`}
          >
            {t(`catPage.kinds.${k}`)}
          </button>
        ))}
      </div>
      <textarea
        required
        minLength={5}
        maxLength={1000}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('catPage.reportPlaceholder')}
        className="rounded-xl border-[1.5px] border-edge bg-surface p-3 text-base text-ink outline-none focus:border-teal"
      />
      {state.error ? <p role="alert" className="m-0 font-bold text-live">{state.error}</p> : null}
      <button type="submit" disabled={state.busy} className="cursor-pointer self-start rounded-full bg-live px-5 py-2.5 font-extrabold text-white disabled:opacity-60">
        {t('catPage.sendReport')}
      </button>
    </form>
  )
}

/** A cat's page: who it is, when and where it comes, its videos, and "it looks unwell". */
export default function CatPage() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { user } = useAuth()
  const navigate = useNavigate()
  const profile = useApi(() => api.catProfile(id), [id, user?.uid])
  const follows = useApi(() => (user ? api.follows() : Promise.resolve([])), [user?.uid])
  const [openClip, setOpenClip] = useState(null)
  const [reporting, setReporting] = useState(false)

  if (profile.loading) return <Loading />
  if (profile.error) return <ErrorState notFound={profile.error.status === 404} />
  const p = profile.data
  const cat = p.cat
  const follow = follows.data?.find((f) => f.cat_id === cat.id)
  const max = Math.max(1, ...p.per_day.map((d) => d.visits))

  const toggle = async () => {
    if (!user) return navigate('/login', { state: { from: `/cats/${id}` } })
    await (follow ? api.unfollow(follow.id) : api.follow({ cat_id: cat.id }))
    follows.reload()
    profile.reload()
    if (!follow) enablePush().catch(() => {})
  }

  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-8 px-4 pt-10 pb-[72px] sm:px-8">
      <Link to="/cats" className="text-sm font-bold text-subtle no-underline hover:text-ink">← {t('nav.cats')}</Link>
      <div className="flex flex-wrap items-start gap-8">
        <img src={cat.photo_url ?? '/brand/urbanpurr-app-icon.svg'} alt={cat.name} className="aspect-square w-[min(320px,100%)] rounded-[28px] object-cover" />
        <div className="flex min-w-[260px] flex-1 flex-col gap-4">
          <h1 className="text-[clamp(36px,5vw,52px)] leading-none font-black tracking-[-1px]">{cat.name}</h1>
          {p.under_care ? (
            <span className="self-start rounded-full bg-mint px-3 py-1.5 text-sm font-extrabold">🩺 {t('catPage.underCare')}</span>
          ) : null}
          <p className="m-0 text-lg leading-normal text-muted">{pickI18n(cat.description, lang)}</p>
          <div className="flex flex-wrap gap-3 text-[15px]">
            <span className="rounded-2xl bg-paper px-4 py-3">
              <b className="block text-2xl">{p.visits_30d}</b>
              {t('catPage.visits30')}
            </span>
            <span className="rounded-2xl bg-paper px-4 py-3">
              <b className="block text-2xl">{p.last_seen ? timeAgo(p.last_seen, lang) : '—'}</b>
              {t('catPage.lastSeen')}
            </span>
            <span className="rounded-2xl bg-paper px-4 py-3">
              <b className="block text-2xl">{p.followers}</b>
              {t('catPage.followers')}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={toggle}
              aria-pressed={Boolean(follow)}
              className={`flex cursor-pointer items-center gap-2 rounded-full border-[1.5px] border-teal px-5 py-3 font-extrabold ${follow ? 'bg-teal text-on-teal' : 'text-teal'}`}
            >
              <HeartIcon filled={Boolean(follow)} /> {follow ? t('cats.followed') : t('cats.follow')}
            </button>
            {p.home_feeder ? (
              <Link to={`/feeders/${p.home_feeder.slug}`} className="rounded-full border-[1.5px] border-edge px-5 py-3 font-extrabold text-ink no-underline">
                📍 {pickI18n(p.home_feeder.name, lang)}
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-black">{t('catPage.visits14')}</h2>
        <div className="flex h-32 items-end gap-1.5" role="img" aria-label={t('catPage.visits14')}>
          {p.per_day.map((d) => (
            <div key={d.day} className="flex flex-1 flex-col items-center gap-1" title={`${d.day}: ${d.visits}`}>
              <div className="w-full rounded-t-md bg-teal-soft" style={{ height: `${(d.visits / max) * 100}%`, minHeight: d.visits ? 4 : 1 }} />
              <span className="text-[10px] text-subtle">{d.day.slice(8)}</span>
            </div>
          ))}
        </div>
        {p.feeders.length ? (
          <p className="m-0 text-muted">
            {t('catPage.seenAt')}{' '}
            {p.feeders.map((f, i) => (
              <span key={f.slug}>
                {i ? ', ' : ''}
                <Link to={`/feeders/${f.slug}`} className="font-bold text-teal">{pickI18n(f.name, lang)}</Link> ({f.visits})
              </span>
            ))}
          </p>
        ) : null}
      </section>

      {p.clips.length ? (
        <section className="flex flex-col gap-4">
          <h2 className="text-2xl font-black">{t('cabinet.feederVideos')}</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4">
            {p.clips.map((c) => (
              <ClipCard key={c.id} clip={c} onOpen={(x) => setOpenClip(x.id)} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-3 border-t border-line pt-6">
        <h2 className="text-xl font-black">{t('catPage.worried')}</h2>
        <p className="m-0 text-muted">{t('catPage.worriedText')}</p>
        {reporting ? (
          <ReportForm catId={cat.id} onDone={profile.reload} />
        ) : (
          <button
            type="button"
            onClick={() => (user ? setReporting(true) : navigate('/login', { state: { from: `/cats/${id}` } }))}
            className="cursor-pointer self-start rounded-full border-[1.5px] border-live px-5 py-2.5 font-extrabold text-live"
          >
            🩺 {t('catPage.report')}
          </button>
        )}
      </section>

      {openClip ? <ClipModal clipId={openClip} onClose={() => setOpenClip(null)} /> : null}
    </main>
  )
}
